import axios from 'axios'
import Cookies from 'js-cookie'
import cacheService from './CacheService'

// Base URL for API requests
// Dynamic base URL for API requests based on current domain
// uaetaxlaws.com, ksataxlaws.com, kuwaittaxlaws.com, qatartaxlaws.com, omantaxlaws.com, bahraintaxlaws.com, localhost - REACT_APP_ENVIRONMENT=dev
// all 6 domains point to the same backend api container running on the same server
// but for seo purpose we dont want to show ksa data on uaetaxlaws.com, similar for other country specific domains so we can't use a common endpoint REACT_APP_BACKEND_URL
// since there will be only 1 PROD, so REACT_APP_BACKEND_URL=gcctaxlaws.com, so will work and we can show all 6 countries data
// also for local environment testing we will show all 6 countries only for localhost and private IP address access

const getDynamicBaseUrl = () => {
  const hostname = window.location.hostname
  // For localhost/development environment, use the hardcoded backend URL
  if (isLocalEnvironment(hostname)) {
    // Use environment variable if available, otherwise fallback to common development URL
    return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080'
  }
  const protocol = window.location.protocol
  const port = window.location.port

  // Construct the base URL using current domain
  let baseUrl = `${protocol}//${hostname}`
  if (port && port !== '80' && port !== '443') {
    baseUrl += `:${port}`
  }

  return baseUrl
}
const isLocalEnvironment = (domain) => {
  // Check for localhost variations
  if (domain === 'localhost' || domain === '127.0.0.1') {
    return true
  }

  // Check for private IP ranges
  if (domain.match(/^192\.168\.\d+\.\d+$/)) return true // 192.168.0.0/16
  if (domain.match(/^10\.\d+\.\d+\.\d+$/)) return true // 10.0.0.0/8
  if (domain.match(/^172\.(1[6-9]|2\d|3[01])\.\d+\.\d+$/)) return true // 172.16.0.0/12

  return false
}
const baseUrl = getDynamicBaseUrl() // process.env.REACT_APP_BACKEND_URL
const DEFAULT_SESSION_HEARTBEAT_TIMEOUT_MS = 8000

/**
 * API service for making HTTP requests
 */
class ApiService {
  constructor() {
    // Create axios instance with default config
    this.axios = axios.create({
      baseURL: baseUrl,
      timeout: 15000, // 15 seconds timeout
      // Reject 4xx/5xx so callers can handle errors in catch blocks (e.g. 402 paywall)
      validateStatus: (status) => status >= 200 && status < 400,
      withCredentials: true, // asking browser to attach all cookies to requests belonging to the base URL
      paramsSerializer: {
        serialize: (params) => this.serializeQueryParams(params),
      },
      // Set default headers
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    })
    this.authContextRef = null
    this.isHandlingUnauthorized = false
    this.activeRequestControllers = new Map()

    // Add request interceptor CSRF and correlationId
    this.axios.interceptors.request.use(
      async (config) => {
        // Generate correlation ID
        const correlationId = `${Date.now()}-gccTaxLaws-ui`
        config.headers['X-Correlation-ID'] = correlationId

        // Only fetch CSRF token for non-safe methods
        const safeMethods = ['GET', 'HEAD', 'OPTIONS']
        if (!safeMethods.includes(config.method.toUpperCase())) {
          let csrfToken = Cookies.get('XSRF-TOKEN')
          if (!csrfToken) {
            try {
              // Use the same axios instance but without interceptors to avoid recursion
              await axios.get(`${baseUrl}/api/v1/csrf-token`, {
                withCredentials: true,
              })

              csrfToken = Cookies.get('XSRF-TOKEN')
            } catch (error) {
              console.error('❌ Failed to fetch CSRF token:', error)
            }
          }
          if (csrfToken) {
            config.headers['X-XSRF-TOKEN'] = csrfToken
          }
        }

        return config
      },
      (error) => {
        console.error('❌ Request interceptor error:', error)
        return Promise.reject(error)
      },
    )

    // Response interceptor for errors handling
    this.axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Handle 401 errors (session expired)
        if (error.response?.status === 401) {
          if (this.isHandlingUnauthorized) {
            return Promise.reject(error)
          }

          const requestUrl = String(error.config?.url || '')
          const isOAuthCallbackRoute =
            typeof window !== 'undefined' &&
            window.location.pathname === '/oauth2/callback/success'
          const isOAuthFlowEndpoint = requestUrl.includes(
            '/api/v1/auth/oauth2/',
          )
          const isOAuthFlowInProgress =
            typeof window !== 'undefined' &&
            sessionStorage.getItem('auth_flow_started') === 'true'

          if (
            isOAuthCallbackRoute ||
            isOAuthFlowEndpoint ||
            isOAuthFlowInProgress
          ) {
            console.warn(
              '⚠️ 401 received during OAuth callback flow, skipping auto-logout to preserve verification session.',
            )
            return Promise.reject(error)
          }

          console.error('🚫 Session expired or unauthorized')
          this.clearAuthAndRedirect()
        }

        // Handle 402 errors (Payment Required)
        if (error.response?.status === 402) {
          console.error('💳 Payment Required:', error.response.data)
          // Trigger quota refresh to update UI
          this.triggerQuotaRefresh()
        }

        // Handle 403 errors
        if (error.response?.status === 403) {
          console.error('🚫 Forbidden access:', error.response.data)
        }
        // Handle network errors
        if (!error.response) {
          console.error('🌐 Network error:', error.message)
          error.userMessage = 'Network error. Please check your connection.'
        }
        return Promise.reject(error)
      },
    )
  }

  isRequestCanceled(error) {
    return (
      axios.isCancel?.(error) ||
      error?.code === 'ERR_CANCELED' ||
      error?.name === 'CanceledError'
    )
  }

  serializeQueryParams(params = {}) {
    if (params instanceof URLSearchParams) {
      return params.toString()
    }

    const searchParams = new URLSearchParams()

    Object.entries(params || {}).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return
      }

      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item === undefined || item === null) {
            return
          }
          const normalized = String(item).trim()
          if (normalized) {
            searchParams.append(key, normalized)
          }
        })
        return
      }

      if (typeof value === 'string') {
        const normalized = value.trim()
        if (normalized) {
          searchParams.append(key, normalized)
        }
        return
      }

      searchParams.append(key, String(value))
    })

    return searchParams.toString()
  }

  buildAbortKey(endpoint, params = {}, options = {}) {
    if (options.abortKey) {
      return String(options.abortKey)
    }
    return cacheService.generateKey(endpoint, params)
  }

  withManagedAbort(endpoint, params = {}, options = {}) {
    const shouldManageAbort = Boolean(options.cancelPrevious || options.abortKey)
    if (!shouldManageAbort) {
      return {
        signal: options.signal,
        cleanup: () => {},
      }
    }

    const abortKey = this.buildAbortKey(endpoint, params, options)

    if (options.cancelPrevious) {
      const previousController = this.activeRequestControllers.get(abortKey)
      if (previousController) {
        previousController.abort()
      }
    }

    const controller = new AbortController()
    let detachExternalAbort = null

    if (options.signal) {
      if (options.signal.aborted) {
        controller.abort()
      } else {
        const forwardAbort = () => controller.abort()
        options.signal.addEventListener('abort', forwardAbort, { once: true })
        detachExternalAbort = () => {
          options.signal.removeEventListener('abort', forwardAbort)
        }
      }
    }

    this.activeRequestControllers.set(abortKey, controller)

    return {
      signal: controller.signal,
      cleanup: () => {
        if (detachExternalAbort) {
          detachExternalAbort()
        }
        const activeController = this.activeRequestControllers.get(abortKey)
        if (activeController === controller) {
          this.activeRequestControllers.delete(abortKey)
        }
      },
    }
  }

  // Set auth context reference for more integrated auth handling

  setAuthContext(authContext) {
    this.authContextRef = authContext
  }

  getCurrentPath() {
    if (typeof window === 'undefined') {
      return null
    }

    const fullPath = `${window.location.pathname}${window.location.search}${window.location.hash || ''}`
    return fullPath.startsWith('/') ? fullPath : null
  }

  shouldCaptureRedirectPath(pathname) {
    return pathname !== '/login' && pathname !== '/oauth2/callback/success'
  }

  captureRedirectPath() {
    if (typeof window === 'undefined') {
      return
    }

    if (!this.shouldCaptureRedirectPath(window.location.pathname)) {
      return
    }

    const currentPath = this.getCurrentPath()
    if (currentPath) {
      sessionStorage.setItem('redirectPath', currentPath)
    }
  }

  setSessionExpiredNotice() {
    if (typeof window === 'undefined') {
      return
    }

    sessionStorage.setItem('auth_notice', 'session_expired')
  }

  redirectToLogin() {
    if (
      typeof window !== 'undefined' &&
      window.location.pathname !== '/login'
    ) {
      window.location.assign('/login')
    }
  }

  clearAuthAndRedirect() {
    if (this.isHandlingUnauthorized) {
      return
    }

    this.isHandlingUnauthorized = true
    this.clearCache()
    this.setSessionExpiredNotice()
    this.captureRedirectPath()

    if (this.authContextRef?.logout) {
      // Session already expired; skip backend logout call for a faster redirect.
      this.authContextRef
        .logout({
          redirectToLogin: true,
          preserveRedirectPath: true,
          skipBackendLogout: true,
        })
        .catch((error) => {
          console.warn('Failed to clear auth context, forcing login redirect', error)
          this.redirectToLogin()
        })
        .finally(() => {
          this.isHandlingUnauthorized = false
        })
      return
    }

    this.redirectToLogin()
    this.isHandlingUnauthorized = false
  }

  /**
   * Handle API errors
   *
   * @param {Error} error Axios error
   * @returns {Promise<Error>} Rejected promise with error
   */
  handleError(error) {
    if (this.isRequestCanceled(error)) {
      error.isCanceled = true
      return Promise.reject(error)
    }

    let errorMessage = 'An unexpected error occurred'

    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      const statusCode = error.response.status
      const errorData = error.response.data || {}

      switch (statusCode) {
        case 400:
          errorMessage =
            'Bad request: ' + (errorData?.message || 'Please check your input')
          break
        case 401:
          if (errorData.code === 'TOKEN_EXPIRED') {
            errorMessage = 'Session expired. Please log in again.'
          } else {
            errorMessage = 'Unauthorized: Please log in again'
          }
          break
        case 402: // Payment Required handling
          errorMessage =
            errorData?.message ||
            'Payment or upgrade required to access this content'
          error.isQuotaError = true // Flag for component handling
          error.quotaData = {
            reason: errorData?.denialReason || 'quota_exhausted',
            quotaResetDate: errorData?.quotaResetDate,
            remainingCredits: errorData?.remainingCredits || 0,
            // Backend may return remainingDtaaQuota for DTAA endpoints
            remainingQuota:
              errorData?.remainingQuota ?? errorData?.remainingDtaaQuota ?? 0,
            upgradeRequired: errorData?.upgradeRequired !== false,
          }
          break
        case 403:
          errorMessage =
            'Forbidden: You do not have permission to access this resource'
          break
        case 404:
          errorMessage = 'Not found: The requested resource does not exist'
          break
        case 423:
          errorMessage = 'Account locked: Too many failed attempts'
          break
        case 429:
          errorMessage = 'Too many requests: Please try again later'
          break
        case 500:
          errorMessage = 'Server error: Please try again later'
          break
        default:
          errorMessage = `Error ${statusCode}: ${
            errorData?.message || 'Something went wrong'
          }`
      }
      console.error('API Error:', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers,
      })
    } else if (error.request) {
      errorMessage = 'Network error: Please check your connection'
    } else {
      errorMessage = 'Request error: ' + error.message
    }

    error.userMessage = errorMessage
    return Promise.reject(error)
  }

  /**
   * Generic method to make a GET request to any endpoint
   *
   * @param {string} endpoint API endpoint
   * @param {Object} params Query parameters
   * @param {Object} options Cache options
   * @returns {Promise<any>} Response data
   */
  async get(endpoint, params = {}, options = {}) {
    try {
      return cacheService.getData(
        async () => {
          const { signal, cleanup } = this.withManagedAbort(
            endpoint,
            params,
            options,
          )

          try {
            const requestConfig = {
              params,
            }
            if (signal) {
              requestConfig.signal = signal
            }
            const response = await this.axios.get(endpoint, requestConfig)
            return response.data
          } finally {
            cleanup()
          }
        },
        endpoint,
        params,
        options,
      )
    } catch (error) {
      return this.handleError(error)
    }
  }

  /**
   * Generic method to make a POST request to any endpoint
   *
   * @param {string} endpoint API endpoint
   * @param {Object} data Request body
   * @param {Object} options Additional axios options
   * @returns {Promise<any>} Response data
   */
  async post(endpoint, data = {}, options = {}) {
    try {
      const response = await this.axios.post(endpoint, data, options)
      return response.data
    } catch (error) {
      return this.handleError(error)
    }
  }

  /**
   * Generic method to make a PUT request to any endpoint
   *
   * @param {string} endpoint API endpoint
   * @param {Object} data Request body
   * @param {Object} options Additional axios options
   * @returns {Promise<any>} Response data
   */
  async put(endpoint, data = {}, options = {}) {
    try {
      const response = await this.axios.put(endpoint, data, options)
      // Defensive check in case validateStatus changes in future
      if (response.status >= 400 && response.status < 500) {
        // Create an error object that matches axios error structure
        const error = new Error(`Request failed with status ${response.status}`)
        error.response = {
          status: response.status,
          data: response.data,
          headers: response.headers,
        }
        // Don't call handleError here - let the catch block handle it
        // so the original error structure is preserved
        throw error
      }
      return response.data
    } catch (error) {
      // handleError adds userMessage but preserves error.response.data structure
      // Since it returns Promise.reject(error), we need to rethrow
      return this.handleError(error)
    }
  }
  async patch(endpoint, data = {}, options = {}) {
    try {
      const response = await this.axios.patch(endpoint, data, options)

      return response.data
    } catch (error) {
      return this.handleError(error)
    }
  }
  /**
   * Generic DELETE method
   */
  async delete(endpoint, options = {}) {
    try {
      const response = await this.axios.delete(endpoint, options)
      return response.data
    } catch (error) {
      return this.handleError(error)
    }
  }
  // Utility method to check if user is authenticated
  isAuthenticated() {
    // For session-based auth, we can't check client-side
    // This will be handled by the AuthContext session check
    return false // Always return false, let AuthContext handle it
  }

  /**
   * Lightweight session heartbeat check.
   * Returns 204 when session is active and 401 when session is expired/invalid.
   */
  async getSessionStatus(options = {}) {
    const timeoutMs =
      Number(options?.timeoutMs) > 0
        ? Number(options.timeoutMs)
        : DEFAULT_SESSION_HEARTBEAT_TIMEOUT_MS

    try {
      const response = await this.axios.get('/api/v1/auth/oauth2/session-status', {
        timeout: timeoutMs,
        validateStatus: (status) => status === 204 || status === 401,
      })

      return {
        isAuthenticated: response.status === 204,
        status: response.status,
      }
    } catch (error) {
      return {
        isAuthenticated: true,
        status: 0,
        error,
      }
    }
  }

  /**
   * Get countries with laws
   *
   * @param {Object} options Cache options
   * @returns {Promise<Array>} Countries data
   */
  async getCountriesWithLaws(options = {}) {
    const endpoint = '/api/v1/countries/with-laws'
    return this.get(
      endpoint,
      {},
      {
        ttl: 60 * 60 * 1000, // 1 hour
        ...options,
      },
    )
  }

  /**
   * Perform a global search across multiple entity types
   *
   * @param {Object} params Search parameters including country, lawShortName, query, articlePage, decisionPage, guidePage
   * @param {Object} options Cache options
   * @returns {Promise<Object>} Combined search results for all entity types
   */
  async globalSearch(params, options = {}) {
    const endpoint = '/api/v1/global-search/'
    return this.get(endpoint, params, {
      ttl: 15 * 60 * 1000, // 15 minutes
      ...options,
    })
  }
  async globalSearchCSPV2(params, options = {}) {
    const endpoint = '/api/v2/global-search/search'
    return this.get(endpoint, params, {
      ttl: 5 * 60 * 1000, // 5 minutes
      ...options,
    })
  }

  async globalSearchTaxLawsAutocomplete(params, options = {}) {
    const endpoint = '/api/v2/global-search/tax-laws/autocomplete'
    return this.get(endpoint, params, {
      ttl: 2 * 60 * 1000, // 2 minutes
      ...options,
    })
  }

  async globalSearchTaxTransparencyAutocomplete(params, options = {}) {
    const endpoint = '/api/v2/global-search/tax-transparency/autocomplete'
    return this.get(endpoint, params, {
      ttl: 2 * 60 * 1000, // 2 minutes
      ...options,
    })
  }

  async globalSearchAutocomplete(params, options = {}) {
    return this.globalSearchTaxTransparencyAutocomplete(params, options)
  }

  async globalSearchAutocompleteV2(params, options = {}) {
    return this.globalSearchTaxTransparencyAutocomplete(params, options)
  }

  async globalSearchAutocompleteByLawV2(params, options = {}) {
    return this.globalSearchTaxLawsAutocomplete(params, options)
  }

  async taxTreatySearchAutocompleteV2(params, options = {}) {
    const endpoint = '/api/v2/tax-treaties/autocomplete'
    return this.get(endpoint, params, {
      ttl: 2 * 60 * 1000, // 2 minutes
      ...options,
    })
  }

  /**
   * Generic method to search entities
   *
   * @param {string} entityType Type of entity to search (articles, chapters, decisions, guidelines)
   * @param {Object} params Search parameters
   * @param {Object} options Cache options
   * @returns {Promise<Object>} Search results
   */
  async searchEntities(entityType, params, options = {}) {
    const entityConfig = {
      articles: { searchEndpoint: '/api/v1/articles/search' },
      chapters: { searchEndpoint: '/api/v1/chapters/search' },
      decisions: { searchEndpoint: '/api/v1/decisions/search' },
      guidelines: { searchEndpoint: '/api/v1/guidelines/search' },
      // Tax treaty search is V2-only via searchTaxTreatiesV2.
    }
    // Check if entity type is supported
    if (!entityConfig[entityType]) {
      throw new Error(`Unsupported entity type: ${entityType}`)
    }
    return this.get(entityConfig[entityType].searchEndpoint, params, options)
  }

  /**
   * Generic method to get entity by slug
   *
   * @param {string} entityType Type of entity to get (articles, chapters, decisions, guidelines, taxTreaties)
   * @param {string} slug Entity slug
   * @param {Object} options Cache options
   * @returns {Promise<Object>} Entity data
   */
  async getEntityBySlug(entityType, slug, options = {}) {
    const entityConfig = {
      articles: { searchEndpoint: '/api/v1/articles/slug' },
      chapters: { searchEndpoint: '/api/v1/chapters/slug' },
      decisions: { searchEndpoint: '/api/v1/decisions/slug' },
      guidelines: { searchEndpoint: '/api/v1/guidelines/slug' },
      taxTreaties: { searchEndpoint: '/api/v1/tax-treaties/slug' },
      blogs: { searchEndpoint: '/api/v1/blogs/slug' },
    }
    // Check if entity type is supported
    if (!entityConfig[entityType]) {
      throw new Error(`Unsupported entity type: ${entityType}`)
    }
    return this.get(
      `${entityConfig[entityType].searchEndpoint}/${slug}`,
      {},
      options,
    )
  }

  /**
   * Get partner countries for tax treaties
   *
   * @param {string} primaryCountry The primary country code/slug
   * @param {Object} options Cache options
   * @returns {Promise<Array>} Partner countries data
   */
  async getTaxTreatyPartnerCountries(primaryCountry, options = {}) {
    const endpoint = `/api/v1/tax-treaties/partner-countries/${primaryCountry}`
    return this.get(endpoint, {}, {
      // Partner country data doesn't change often, so cache for a longer time
      ttl: 60 * 60 * 1000, // 1 hour
      ...options,
    })
  }

  // Maintain backwards compatibility with the existing API
  async searchArticles(params, options = {}) {
    return this.searchEntities('articles', params, options)
  }

  async searchChapters(params, options = {}) {
    return this.searchEntities('chapters', params, options)
  }

  async searchDecisions(params, options = {}) {
    return this.searchEntities('decisions', params, options)
  }

  async searchGuidelines(params, options = {}) {
    return this.searchEntities('guidelines', params, options)
  }

  async searchTaxTreatiesV2(params, options = {}) {
    const endpoint = '/api/v2/tax-treaties/search'
    return this.get(endpoint, params, {
      ttl: 5 * 60 * 1000, // 5 minutes
      ...options,
    })
  }

  async globalSearchAllV1(params, options = {}) {
    const endpoint = '/api/v1/global-search/all'
    return this.get(endpoint, params, {
      ttl: 5 * 60 * 1000,
      ...options,
    })
  }

  async searchTaxTreatiesV1(params, options = {}) {
    const endpoint = '/api/v1/tax-treaties/search'
    return this.get(endpoint, params, {
      ttl: 5 * 60 * 1000,
      ...options,
    })
  }

  async getArticleBySlug(slug, options = {}) {
    return this.getEntityBySlug('articles', slug, options)
  }

  async getChaptersBySlug(slug, options = {}) {
    return this.getEntityBySlug('chapters', slug, options)
  }

  async getDecisionBySlug(slug, options = {}) {
    return this.getEntityBySlug('decisions', slug, options)
  }

  async getGuidelineBySlug(slug, options = {}) {
    return this.getEntityBySlug('guidelines', slug, options)
  }

  async getTaxTreatyBySlug(slug, options = {}) {
    return this.getEntityBySlug('taxTreaties', slug, options)
  }

  async getBlogBySlug(slug, options = {}) { 
    return this.getEntityBySlug('blogs', slug, options)
  }

  // Get all users (admin only)
  async getUsers(params = {}) {
    const endpoint = '/api/v1/admin/users'
    return this.get(endpoint, params)
  }

  // Get user by ID (admin only)
  async getUserById(userId) {
    const endpoint = `/api/v1/admin/users/${userId}`
    return this.get(endpoint)
  }

  // Search users (admin only)
  async searchUsers(query, limit = 10) {
    const endpoint = '/api/v1/admin/users/search'
    return this.get(endpoint, { query, limit })
  }

  // Add role to user (admin only)
  async addRoleToUser(userId, roleName) {
    const endpoint = `/api/v1/admin/users/${userId}/roles/${roleName}`
    return this.post(endpoint)
  }

  // Remove role from user (admin only)
  async removeRoleFromUser(userId, roleName) {
    const endpoint = `/api/v1/admin/users/${userId}/roles/${roleName}`
    return this.delete(endpoint)
  }

  // Activate user account (admin only)
  async activateUser(userId) {
    const endpoint = `/api/v1/admin/users/${userId}/activate`
    return this.post(endpoint)
  }

  // Deactivate user account (admin only)
  async deactivateUser(userId) {
    const endpoint = `/api/v1/admin/users/${userId}/deactivate`
    return this.post(endpoint)
  }

  // Unlock user account (admin only)
  async unlockUser(userId) {
    const endpoint = `/api/v1/admin/users/${userId}/unlock`
    return this.post(endpoint)
  }

  // Verify user account (admin only)
  async verifyUser(userId) {
    const endpoint = `/api/v1/admin/users/${userId}/verify`
    return this.post(endpoint)
  }

  // Get admin dashboard statistics (admin only)
  async getAdminStats() {
    const endpoint = '/api/v1/admin/stats'
    return this.get(
      endpoint,
      {},
      {
        bypassCache: true,
        skipCache: true,
      },
    )
  }

  async getAdminSubscriptionMetrics() {
    return this.get(
      '/api/v1/admin/monetization/subscriptions/metrics',
      {},
      {
        bypassCache: true,
        skipCache: true,
      },
    )
  }

  async getAdminSubscriptions(params = {}) {
    return this.get('/api/v1/admin/monetization/subscriptions', params, {
      bypassCache: true,
      skipCache: true,
    })
  }

  // Get users by role (admin only)
  async getUsersByRole(roleName) {
    const endpoint = `/api/v1/admin/users/role/${roleName}`
    return this.get(endpoint)
  }

  async getUserActivity(params = {}) {
    return this.get('/api/v1/profile/activity', params)
  }

  async getUserSearchActivities(params = {}, options = {}) {
    return this.get('/api/v1/profile/search-activities', params, options)
  }

  // Public, cross-user "Trending Searches" feed — anonymous-safe, cached
  // server-side. Used by the homepage section that's visible to everyone.
  async getTrendingSearches(params = {}, options = {}) {
    return this.get('/api/v1/trending-searches', params, options)
  }

  async getUserBookmarks(params = {}) {
    return this.get('/api/v1/profile/bookmarks', params)
  }

  async getUserStats() {
    return this.get('/api/v1/profile/stats')
  }

  async sendProfileContentShareVerificationCode(email) {
    return this.post('/api/v1/profile/content-share/email-verification/send', {
      email,
    })
  }

  async verifyProfileContentShareCodeAndImport({
    email,
    otp,
    includeBookmarks = true,
    includeAnnotations = true,
  }) {
    return this.post(
      '/api/v1/profile/content-share/email-verification/verify-and-import',
      {
        email,
        otp,
        includeBookmarks,
        includeAnnotations,
      }
    )
  }

  // Bookmark Management
  async getBookmarks(params = {}) {
    return this.get('/api/v1/bookmarks', params)
  }

  async getBookmarkByArticle(articleSlug) {
    return this.get(`/api/v1/bookmarks/article/${articleSlug}`)
  }

  async createBookmark(bookmarkData) {
    return this.post('/api/v1/bookmarks', bookmarkData)
  }

  async deleteBookmark(bookmarkId) {
    return this.delete(`/api/v1/bookmarks/${bookmarkId}`)
  }

  async deleteBookmarkByArticle(articleSlug) {
    return this.delete(`/api/v1/bookmarks/article/${articleSlug}`)
  }

  // Annotation Management
  /**
   * Create a new annotation for a document
   * @param {Object} annotationData - Annotation creation data
   * @returns {Promise<Object>} - Created annotation
   */
  async createAnnotation(annotationData) {
    try {
      const response = await this.post('/api/v1/annotations', annotationData)
      return response // Wrap in data property for consistency
    } catch (error) {
      console.error('Error creating annotation:', error)
      throw error
    }
  }

  /**
   * Get all annotations for a specific document
   * @param {string} documentId - Document ID
   * @returns {Promise<Array>} - Array of annotations
   */
  async getAnnotationsForDocument(documentId) {
    try {
      const response = await this.get(
        `/api/v1/annotations/document/${documentId}`,
      )
      return response // Wrap in data property for consistency
    } catch (error) {
      console.error('Error fetching document annotations:', error)
      throw error
    }
  }

  /**
   * Get user annotations with pagination
   * @param {number} page - Page number (0-based)
   * @param {number} size - Page size
   * @param {string} sortBy - Sort field
   * @param {string} sortDir - Sort direction (asc/desc)
   * @returns {Promise<Object>} - Paginated annotations
   */
  async getUserAnnotations(
    page = 0,
    size = 10,
    sortBy = 'createdAt',
    sortDir = 'desc',
  ) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
        sortBy,
        sortDir,
      })

      const response = await this.get(
        `/api/v1/annotations/user?${params.toString()}`,
      )
      return response
    } catch (error) {
      console.error('Error fetching user annotations:', error)
      throw error
    }
  }
  /**
   * Enhanced search for user annotations with filters
   */
  async searchUserAnnotations(params) {
    try {
      // Build query string, excluding null/undefined values
      const queryParams = new URLSearchParams()

      Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          if (Array.isArray(value)) {
            value.forEach((v) => queryParams.append(key, v))
          } else {
            queryParams.append(key, value)
          }
        }
      })

      console.log(
        'API call:',
        `/api/v1/annotations/search?${queryParams.toString()}`,
      )

      const response = await this.get(
        // ✅ Use this.get instead of api.get
        `/api/v1/annotations/search?${queryParams.toString()}`,
      )
      return response // ✅ Return response directly (already unwrapped by this.get)
    } catch (error) {
      console.error('Error searching user annotations:', error)
      throw error
    }
  }

  /**
   * Get a specific annotation by ID
   * @param {string} annotationId - Annotation ID
   * @returns {Promise<Object>} - Annotation data
   */
  async getAnnotationById(annotationId) {
    return this.get(`/api/v1/annotations/${annotationId}`)
  }

  /**
   * Update an existing annotation
   * @param {string} annotationId - Annotation ID
   * @param {Object} updateData - Annotation update data
   * @returns {Promise<Object>} - Updated annotation
   */
  async updateAnnotation(annotationId, updateData) {
    try {
      const response = await this.put(
        `/api/v1/annotations/${annotationId}`,
        updateData,
      )
      return response // Wrap in data property for consistency
    } catch (error) {
      console.error('Error updating annotation:', error)
      throw error
    }
  }

  /**
   * Delete an annotation
   * @param {string} annotationId - Annotation ID
   * @returns {Promise<void>} - Delete confirmation
   */
  async deleteAnnotation(annotationId) {
    try {
      const response = await this.delete(`/api/v1/annotations/${annotationId}`)
      return response
    } catch (error) {
      console.error('Error deleting annotation:', error)
      throw error
    }
  }
  /**
   * Search annotations by content
   * @param {string} query - Search query
   * @returns {Promise<Array>} - Array of matching annotations
   */
  async searchAnnotations(query) {
    try {
      const searchParams = {
        query,
        page: 0,
        size: 1000, // Get all results for simple search
        sortBy: 'createdAt',
        sortDir: 'desc',
      }

      const response = await this.searchUserAnnotations(searchParams)
      return response.content || response || []
    } catch (error) {
      console.error('Error in legacy search annotations:', error)
      throw error
    }
  }

  /**
   * Get annotation statistics for the user
   * @returns {Promise<Object>} - Annotation statistics
   */
  async getAnnotationStats() {
    try {
      const response = await this.get('/api/v1/annotations/stats')
      return response
    } catch (error) {
      console.error('Error fetching annotation stats:', error)
      // Return default stats on error
      return {
        total: 0,
        important: 0,
        byCategory: {},
        byPriority: {},
        recentCount: 0,
      }
    }
  }
  async getUserTags(limit = 20) {
    try {
      const response = await this.get(`/api/v1/annotations/tags?limit=${limit}`)
      return response
    } catch (error) {
      console.error('Error fetching user tags:', error)
      return []
    }
  }
  // async bulkUpdateAnnotations(annotationIds, updateData) {
  //   try {
  //     const response = await this.put('/api/v1/annotations/bulk', {
  //       annotationIds,
  //       updateData,
  //     })
  //     return response
  //   } catch (error) {
  //     console.error('Error in bulk update annotations:', error)
  //     throw error
  //   }
  // }
  async bulkDeleteAnnotations(annotationIds) {
    try {
      const response = await this.delete('/api/v1/annotations/bulk', {
        data: { annotationIds },
      })
      return response
    } catch (error) {
      console.error('Error in bulk delete annotations:', error)
      throw error
    }
  }
  // async exportAnnotations(format = 'json', filters = {}) {
  //   try {
  //     const params = new URLSearchParams()
  //     params.append('format', format)

  //     // Add filter parameters
  //     Object.keys(filters).forEach((key) => {
  //       if (filters[key] !== null && filters[key] !== undefined) {
  //         if (Array.isArray(filters[key])) {
  //           filters[key].forEach((value) => params.append(key, value))
  //         } else {
  //           params.append(key, filters[key].toString())
  //         }
  //       }
  //     })

  //     const response = await this.get(
  //       `/api/v1/annotations/export?${params.toString()}`
  //     )
  //     return response
  //   } catch (error) {
  //     console.error('Error exporting annotations:', error)
  //     throw error
  //   }
  // }

  // Activity Tracking
  // backend not implemented yet, since another endpoint used, similar method written in ActivityTracker trackSessionEnd
  // async trackSessionEnd(data) {
  //   return this.post('/api/v1/activity/session-end', data)
  // }

  // Admin Methods
  async getAdminUsers(params = {}) {
    return this.get('/api/v1/admin/users', params)
  }

  async getAdminUserProfile(userId, options = {}) {
    return this.get(
      `/api/v1/admin/users/${encodeURIComponent(userId)}/profile`,
      {},
      options,
    )
  }

  async getAdminUserBookmarks(userId, params = {}, options = {}) {
    return this.get(
      `/api/v1/admin/users/${encodeURIComponent(userId)}/bookmarks`,
      params,
      options,
    )
  }

  async searchAdminUserAnnotations(userId, params = {}) {
    const queryParams = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach((item) => queryParams.append(key, item))
        } else {
          queryParams.append(key, value)
        }
      }
    })

    return this.get(
      `/api/v1/admin/users/${encodeURIComponent(
        userId,
      )}/annotations/search?${queryParams.toString()}`,
    )
  }

  async getAdminUserSubscriptionHistory(userId, options = {}) {
    return this.get(
      `/api/v1/admin/users/${encodeURIComponent(userId)}/subscriptions/history`,
      {},
      options,
    )
  }

  async getAdminUserPaymentHistory(userId, options = {}) {
    return this.get(
      `/api/v1/admin/users/${encodeURIComponent(userId)}/payments/history`,
      {},
      options,
    )
  }

  async getAdminUserQuotaStatus(userId, options = {}) {
    return this.get(
      `/api/v1/admin/users/${encodeURIComponent(userId)}/quota/status`,
      {},
      options,
    )
  }

  async updateAdminUserQuota(userId, quotaData) {
    return this.put(
      `/api/v1/admin/users/${encodeURIComponent(userId)}/quota`,
      quotaData
    )
  }

  async createAdminUser(userData) {
    return this.post('/api/v1/admin/users', userData)
  }

  async updateAdminUser(userId, userData) {
    return this.put(`/api/v1/admin/users/${userId}`, userData)
  }

  async deleteAdminUser(userId) {
    return this.delete(`/api/v1/admin/users/${userId}`)
  }

  async getAdminActivity(params = {}) {
    return this.get('/api/v1/admin/activity', params)
  }

  async getAdminBlogs(params = {}) {
    return this.get('/api/v1/admin/blogs', params, {
      bypassCache: true,
      skipCache: true,
    })
  }

  async getAdminBlogsMetrics() {
    return this.get(
      '/api/v1/admin/blogs/metrics',
      {},
      {
        bypassCache: true,
        skipCache: true,
      },
    )
  }

  async createAdminBlog(blogData) {
    return this.post('/api/v1/admin/blogs', blogData)
  }

  async updateAdminBlog(blogId, blogData) {
    return this.put(`/api/v1/admin/blogs/${blogId}`, blogData)
  }

  async deleteAdminBlog(blogId) {
    return this.delete(`/api/v1/admin/blogs/${blogId}`)
  }

  async reviewAdminBlog(blogId, reviewData) {
    return this.post(`/api/v1/admin/blogs/${blogId}/review`, reviewData)
  }

  // Email Management Methods
  async getEmailTemplates(params = {}) {
    return this.get('/api/v1/admin/email/templates', params)
  }

  async createEmailTemplate(templateData) {
    return this.post('/api/v1/admin/email/templates', templateData)
  }

  async updateEmailTemplate(templateId, templateData) {
    return this.put(`/api/v1/admin/email/templates/${templateId}`, templateData)
  }

  async deleteEmailTemplate(templateId) {
    return this.delete(`/api/v1/admin/email/templates/${templateId}`)
  }

  async sendEmail(emailData) {
    return this.post('/api/v1/admin/email/send', emailData)
  }

  async getEmailHistory(params = {}) {
    return this.get('/api/v1/admin/email/history', params, {
      bypassCache: true,
      skipCache: true,
    })
  }

  async getEmailHistoryById(emailHistoryId) {
    return this.get(`/api/v1/admin/email/history/${emailHistoryId}`)
  }

  async getRecipientCount(recipientType) {
    return this.get('/api/v1/admin/email/recipients/count', { recipientType })
  }

  async retryFailedEmails(emailHistoryId) {
    return this.post(`/api/v1/admin/email/history/${emailHistoryId}/retry`)
  }

  async unsubscribeEmail(email, options = {}) {
    try {
      const params = { email, t: Date.now() }
      if (options?.exp) {
        params.exp = options.exp
      }
      if (options?.sig) {
        params.sig = options.sig
      }

      const response = await this.axios.get('/api/v1/email/unsubscribe', {
        params,
        headers: {
          Accept: 'text/html, text/plain, */*',
        },
        responseType: 'text',
      })
      return response.data
    } catch (error) {
      if (typeof error?.response?.data === 'string' && error.response.data.trim()) {
        error.userMessage = error.response.data.trim()
        return Promise.reject(error)
      }
      return this.handleError(error)
    }
  }

  async sendResubscribeVerificationCode(email) {
    return this.post('/api/v1/email/resubscribe/send', { email })
  }

  async verifyResubscribeVerificationCode({ email, otp }) {
    return this.post('/api/v1/email/resubscribe/verify', { email, otp })
  }

  /**
   * Get all published blogs with pagination
   * @param {number} page - Page number (1-based)
   * @param {number} pageSize - Number of blogs per page
   * @returns {Promise<Object>} - Paginated blog response
   */
  async getAllBlogs(page = 1, pageSize = 6, section = null) {
    const params = { page, pageSize }
    if (section) {
      params.section = section
    }
    return this.get(`/api/v1/blogs`, params)
  }

  /**
   * Get blog by slug
   * @param {string} slug - Blog slug
   * @returns {Promise<Object>} - Blog content
   */
  async getBlogBySlug(slug) {
    return this.get(`/api/v1/blogs/slug/${encodeURIComponent(slug)}`)
  }

  normalizePublicUsername(username) {
    if (!username || typeof username !== 'string') {
      return ''
    }
    const trimmed = username.trim()
    if (!trimmed) {
      return ''
    }
    return trimmed.startsWith('@') ? trimmed.slice(1) : trimmed
  }

  async getPublicProfileByUsername(username, options = {}) {
    const normalizedUsername = this.normalizePublicUsername(username)
    return this.get(
      `/api/v1/profile/public/${encodeURIComponent(normalizedUsername)}`,
      {},
      options,
    )
  }

  async followUserByUsername(username) {
    const normalizedUsername = this.normalizePublicUsername(username)
    return this.post(
      `/api/v1/profile/public/${encodeURIComponent(normalizedUsername)}/follow`,
    )
  }

  async unfollowUserByUsername(username) {
    const normalizedUsername = this.normalizePublicUsername(username)
    return this.delete(
      `/api/v1/profile/public/${encodeURIComponent(normalizedUsername)}/follow`,
    )
  }

  async getFollowersByUsername(
    username,
    page = 1,
    pageSize = 20,
    options = {},
  ) {
    const normalizedUsername = this.normalizePublicUsername(username)
    return this.get(
      `/api/v1/profile/public/${encodeURIComponent(normalizedUsername)}/followers`,
      { page, pageSize },
      options,
    )
  }

  async getFollowingByUsername(
    username,
    page = 1,
    pageSize = 20,
    options = {},
  ) {
    const normalizedUsername = this.normalizePublicUsername(username)
    return this.get(
      `/api/v1/profile/public/${encodeURIComponent(normalizedUsername)}/following`,
      { page, pageSize },
      options,
    )
  }

  async getBlogsByUsername(username, page = 1, pageSize = 6, options = {}) {
    const normalizedUsername = this.normalizePublicUsername(username)
    return this.get(
      `/api/v1/blogs/author/${encodeURIComponent(normalizedUsername)}`,
      { page, pageSize },
      options,
    )
  }

  /**
   * Search blogs
   * @param {string} query - Search query
   * @param {number} page - Page number (1-based)
   * @param {number} pageSize - Number of blogs per page
   * @returns {Promise<Object>} - Paginated search results
   */
  async searchBlogs(query, page = 1, pageSize = 6) {
    return this.searchBlogsBySection(query, page, pageSize, null)
  }

  async searchBlogsBySection(query, page = 1, pageSize = 6, section = null) {
    const params = { query, page, pageSize }
    if (section) {
      params.section = section
    }
    return this.get('/api/v1/blogs/search', params)
  }

  /**
   * Get blogs by category
   * @param {string} category - Blog category
   * @param {number} page - Page number (1-based)
   * @param {number} pageSize - Number of blogs per page
   * @param {string} excludeSlug - Slug to exclude from results
   * @returns {Promise<Object>} - Paginated blog response
   */
  async getBlogsByCategory(
    category,
    page = 1,
    pageSize = 6,
    excludeSlug = null,
    section = null,
  ) {
    const params = { page, pageSize }
    if (excludeSlug) {
      params.exclude = excludeSlug
    }
    if (section) {
      params.section = section
    }
    return this.get(
      `/api/v1/blogs/category/${encodeURIComponent(category)}`,
      params,
    )
  }

  /**
   * Get all blog categories
   * @returns {Promise<Array>} - Array of categories
   */
  async getAllCategories() {
    return this.get(`/api/v1/blogs/categories`)
  }

  /**
   * Create new blog (admin only)
   * @param {Object} blogData - Blog creation data
   * @returns {Promise<Object>} - Created blog
   */
  async createBlog(blogData) {
    return this.post('/api/v1/blogs', blogData)
  }

  /**
   * Update blog (admin only)
   * @param {Object} updateData - Blog update data
   * @returns {Promise<Object>} - Update response
   */
  async updateBlog(updateData) {
    return this.put(`/api/v1/blogs/update`, updateData)
  }

  /**
   * Delete blog (admin only)
   * @param {string} id - Blog ID
   * @returns {Promise<string>} - Delete confirmation message
   */
  async deleteBlog(id) {
    return this.delete(`/api/v1/blogs/${id}`)
  }

  async saveBlogDraft(draftData, blogId = null) {
    if (blogId) {
      return this.put(`/api/v1/blogs/write/${blogId}/draft`, draftData)
    }
    return this.post('/api/v1/blogs/write/draft', draftData)
  }

  async submitBlogForReview(blogId) {
    return this.post(`/api/v1/blogs/write/${blogId}/submit`)
  }

  async getMyBlogs(page = 1, pageSize = 10, options = {}) {
    return this.get(
      '/api/v1/blogs/write/mine',
      { page, pageSize },
      {
        bypassCache: true,
        skipCache: true,
        ...options,
      },
    )
  }

  async getMyBlogById(blogId, options = {}) {
    return this.get(
      `/api/v1/blogs/write/${encodeURIComponent(blogId)}`,
      {},
      options,
    )
  }

  async withdrawBlogFromReview(blogId) {
    return this.post(
      `/api/v1/blogs/write/${encodeURIComponent(blogId)}/withdraw`,
    )
  }

  async deleteMyDraftBlog(blogId) {
    return this.delete(`/api/v1/blogs/write/${encodeURIComponent(blogId)}/draft`)
  }

  async getAdminBlogById(blogId, options = {}) {
    return this.get(`/api/v1/blogs/${encodeURIComponent(blogId)}`, {}, options)
  }

  async getBlogComments(slug, options = {}) {
    return this.get(
      `/api/v1/blogs/slug/${encodeURIComponent(slug)}/comments`,
      {},
      {
        bypassCache: true,
        skipCache: true,
        ...options,
      },
    )
  }

  async addBlogComment(slug, commentData) {
    return this.post(
      `/api/v1/blogs/slug/${encodeURIComponent(slug)}/comments`,
      commentData,
    )
  }

  async updateBlogComment(slug, commentId, commentData) {
    return this.put(
      `/api/v1/blogs/slug/${encodeURIComponent(slug)}/comments/${encodeURIComponent(commentId)}`,
      commentData,
    )
  }

  async deleteBlogComment(slug, commentId) {
    return this.delete(
      `/api/v1/blogs/slug/${encodeURIComponent(slug)}/comments/${encodeURIComponent(commentId)}`,
    )
  }

  async toggleBlogCommentLike(slug, commentId) {
    return this.post(
      `/api/v1/blogs/slug/${encodeURIComponent(slug)}/comments/${encodeURIComponent(commentId)}/likes/toggle`,
    )
  }

  async toggleBlogLike(slug) {
    return this.post(
      `/api/v1/blogs/slug/${encodeURIComponent(slug)}/likes/toggle`,
    )
  }

  async uploadBlogImage(
    file,
    { blogId = null, draftId = null, assetType = 'INLINE' } = {},
  ) {
    const formData = new FormData()
    formData.append('file', file)
    const params = {}
    if (blogId) params.blogId = blogId
    if (draftId) params.draftId = draftId
    if (assetType) params.assetType = assetType
    return this.post('/api/v1/blogs/media/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params,
    })
  }

  async uploadProfileCoverImage(file) {
    const formData = new FormData()
    formData.append('file', file)
    return this.post('/api/v1/profile/media/cover', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  /**
   * Enhanced GET method with quota refresh callback
   */
  async getWithQuotaRefresh(endpoint, params = {}, options = {}) {
    try {
      const response = await this.get(endpoint, params, options)

      // If response contains quota metadata, update context
      if (
        response &&
        (response.linksFiltered !== undefined || response.accessSource)
      ) {
        this.handleQuotaUpdate(response)
      }

      return response
    } catch (error) {
      // 402 quota refresh is already handled by the response interceptor in TaxTreatyArticle.
      // if (error.response?.status === 402) {
      //   this.triggerQuotaRefresh()
      // }
      throw error
    }
  }

  /**
   * Handle quota updates from API responses
   */
  handleQuotaUpdate(response, immediate = false) {
    if (this.authContextRef?.refreshQuota) {
      if (immediate) {
        // For quota deductions, refresh immediately
        this.authContextRef.refreshQuota()
      } else {
        // For other operations, debounce
        clearTimeout(this._quotaRefreshTimeout)
        this._quotaRefreshTimeout = setTimeout(() => {
          this.authContextRef.refreshQuota()
        }, 1000)
      }
    }
  }

  /**
   * Trigger immediate quota refresh
   */
  triggerQuotaRefresh() {
    if (this.authContextRef?.refreshQuota) {
      this.authContextRef.refreshQuota()
    }
  }

  /**
   * Get article with quota handling
   */
  async getArticleBySlugWithQuota(slug, request) {
    const clientIp = this.extractClientIp(request)
    const userAgent = navigator.userAgent

    try {
      const response = await this.get(`/api/v1/articles/slug/${slug}`)

      // Handle quota metadata in response
      if (response.linksFiltered) {
        console.warn('Article links have been filtered due to quota limits')
      }

      // Trigger quota refresh after successful access
      this.handleQuotaUpdate(response)

      return response
    } catch (error) {
      if (error.response?.status === 402) {
        // This shouldn't happen for articles (they don't block access)
        // But handle it gracefully anyway
        console.error('Unexpected 402 for article:', error)
      }
      throw error
    }
  }

  /**
   * Get DTAA with quota handling
   */
  async getTaxTreatyBySlugWithQuota(slug) {
    try {
      const response = await this.get(`/api/v1/tax-treaties/slug/${slug}`)

      // Trigger quota refresh after successful access
      this.handleQuotaUpdate(response)

      return response
    } catch (error) {
      if (error.response?.status === 402) {
        // DTAA access blocked - this is expected behavior
        console.warn('DTAA access blocked due to quota limits')
      }
      throw error
    }
  }

  /**
   * Helper to extract client IP (for future use)
   */
  extractClientIp(request) {
    // In browser context, we can't access real IP
    // This will be handled server-side
    return null
  }

  /**
   * SUBSCRIPTION MANAGEMENT METHODS
   */

  /**
   * Get all active subscription plans
   */
  async getSubscriptionPlans() {
    return this.get('/api/v1/subscriptions/plans')
  }

  /**
   * Get plan by ID
   */
  async getSubscriptionPlanById(planId) {
    return this.get(`/api/v1/subscriptions/plans/${planId}`)
  }

  async getSubscriptionPlanByCode(planCode) {
    return this.get(
      `/api/v1/subscriptions/plans/code/${encodeURIComponent(planCode)}`,
    )
  }

  /**
   * Get user's active subscriptions
   */
  async getUserActiveSubscriptions() {
    return this.get('/api/v1/subscriptions/my-subscriptions')
  }

  /**
   * Get user's subscription history
   */
  async getUserSubscriptionHistory() {
    return this.get('/api/v1/subscriptions/my-subscriptions/history')
  }

  async getUserPaymentHistory(options = {}) {
    return this.get('/api/v1/payments/history', {}, options)
  }

  /**
   * Cancel subscription
   * @param {string} subscriptionId - Subscription ID to cancel
   * @param {string} reason - Cancellation reason
   */
  async cancelSubscription(
    subscriptionId,
    reason = 'User requested cancellation',
  ) {
    return this.post('/api/v1/subscriptions/cancel', {
      subscriptionId: subscriptionId,
      reason: reason,
    })
  }

  /**
   * Update auto-renewal setting
   * @param {string} subscriptionId - Subscription ID
   * @param {boolean} autoRenew - Enable/disable auto-renewal
   */
  async updateSubscriptionAutoRenew(subscriptionId, autoRenew) {
    return this.put(
      `/api/v1/subscriptions/${subscriptionId}/auto-renew?autoRenew=${autoRenew}`,
    )
  }

  /**
   * Purchase subscription
   * @param {Object} purchaseData - Subscription purchase data
   */
  async purchaseSubscription(purchaseData) {
    return this.post('/api/v1/subscriptions/purchase', purchaseData)
  }

  async getAdminOrders(params = {}) {
    return this.get('/api/v1/admin/orders', params)
  }
  /**
   * Deduct quota for link click
   * @param {Object} deductData - Link click deduction data
   * @returns {Promise<DeductQuotaResponse>} - Deduction response
   */
  async deductLinkClickQuota(deductData) {
    try {
      console.log('🔵 Calling POST /api/v1/quota/deduct with:', deductData)

      const response = await this.post('/api/v1/quota/deduct', {
        usageType: 'LINK_CLICK',
        resourceUrl: deductData.resourceUrl,
        documentSlug: deductData.documentSlug || '',
        documentTitle: deductData.documentTitle || '',
        country: deductData.country || this.extractCountryFromUrl(),
        ipAddress: deductData.ipAddress || null,
        userAgent: deductData.userAgent || navigator.userAgent,
      })

      console.log('✅ Deduct API response:', response)

      // Trigger quota refresh after successful deduction
      this.handleQuotaUpdate(response, true)

      return {
        allowed: response.allowed,
        message: response.message,
        deductionSource: response.deductionSource,
        remainingLinkQuota: response.remainingLinkClicks,
        remainingDtaaViews: response.remainingDtaaViews,
        remainingCredits: response.remainingCredits,
        quotaResetDate: response.quotaResetDate,
      }
    } catch (error) {
      console.error('❌ Deduct API error:', error)

      const responseStatus = error.response?.status
      const responseData = error.response?.data || {}
      const backendMessage =
        responseData?.message ||
        responseData?.errorMessage ||
        error.message ||
        'Quota exhausted'
      const isQuotaExhaustedResponse =
        responseStatus === 402 ||
        (responseStatus === 500 &&
          typeof backendMessage === 'string' &&
          backendMessage.toLowerCase().includes('insufficient quota'))

      if (isQuotaExhaustedResponse) {
        console.warn('Quota deduction denied by backend')
        this.triggerQuotaRefresh()

        // Return the error response from backend
        return {
          allowed: false,
          message: backendMessage || 'Quota exhausted',
          denialReason: responseData?.denialReason || 'quota_exhausted',
          suggestUpgrade: responseData?.suggestUpgrade ?? true,
          quotaResetDate: responseData?.quotaResetDate,
          remainingLinkQuota: 0,
          remainingDtaaViews: 0,
          remainingCredits: responseData?.remainingCredits ?? 0,
        }
      }

      throw error
    }
  }

  /**
   * Helper to extract country from current URL
   */
  extractCountryFromUrl() {
    const pathname = window.location.pathname
    const match = pathname.match(/\/(uae|ksa|kuwait|qatar|oman|bahrain|gcc)/)
    return match ? match[1].toUpperCase() : 'UAE'
  }

  async updateAdminOrder(orderId, orderData) {
    return this.put(`/api/v1/admin/orders/${orderId}`, orderData)
  }

  /**
   * Clear all API cache
   */
  clearCache() {
    cacheService.clear()
  }

  /**
   * Clear cache for a specific endpoint
   *
   * @param {string} endpoint API endpoint
   */
  clearEndpointCache(endpoint) {
    cacheService.clearEndpoint(endpoint)
  }

  /**
   * Fetch paginated news articles for a given country.
   * @param {object} params
   * @param {string} params.country   - Country slug (uae | ksa | kuwait | qatar | oman | bahrain)
   * @param {string} [params.search]  - Optional keyword search
   * @param {number} [params.page]    - Page number (default 1)
   * @param {number} [params.pageSize] - Items per page (default 10)
   * @param {string[]} [params.subSource] - Exact source names
   * @param {string[]} [params.category] - Exact category names
   * @returns {Promise<{items: object[], page: number, pageSize: number, totalItems: number, totalPages: number, hasNext: boolean}>}
   */
  async fetchCountryNews({
    country,
    search = '',
    page,
    pageSize,
    sourceType,
    subSource,
    category,
    sortOrder,
    dateFrom,
    dateTo,
  }) {
    const params = { country }
    if (page !== undefined) params.page = page
    if (pageSize !== undefined) params.pageSize = pageSize

    if (search?.trim()) {
      params.search = search.trim()
    }
    if (sourceType) params.sourceType = sourceType
    if (subSource) params.subSource = subSource
    if (category) params.category = category
    if (sortOrder) params.sortOrder = sortOrder
    if (dateFrom) params.dateFrom = dateFrom
    if (dateTo) params.dateTo = dateTo

    return this.get('/api/v1/news', params, {
      ttl: 2 * 60 * 1000, // 2 minutes
    })
  }

  /**
   * Fetch news articles pending admin review (confidence < 65 or manually filtered).
   * @param {Object} params
   * @param {number} [params.page]           - 0-based page index
   * @param {number} [params.size]           - Page size
   * @param {string} [params.status]         - Review status (e.g. 'PENDING_REVIEW')
   * @param {string[]} [params.country]      - Filter by country codes
   * @param {string[]} [params.source]       - Filter by exact source names
   * @param {string} [params.search]         - Keyword search
   * @param {string} [params.sortBy]         - Sort field (newsConfidence|pubDate|country|source)
   * @param {string} [params.sortDir]        - Sort direction (asc|desc)
   * @param {number} [params.confidenceMin]  - Minimum confidence score
   * @param {number} [params.confidenceMax]  - Maximum confidence score
   * @param {string} [params.dateFrom]       - ISO date string (inclusive)
   * @param {string} [params.dateTo]         - ISO date string (inclusive)
   * @returns {Promise<{content: object[], totalPages: number, totalElements: number}>}
   */
  async getAdminNewsForReview(params = {}) {
    return this.get('/api/v1/admin/news/review', params, {
      bypassCache: true,
      skipCache: true,
    })
  }

  /**
   * Fetch government documents pending admin review.
   * @param {Object} params
   * @param {number} [params.page]           - 0-based page index
   * @param {number} [params.size]           - Page size
   * @param {string[]} [params.source]       - Filter by source names
   * @param {string[]} [params.countries]    - Filter by country codes
   * @param {string[]} [params.taxType]      - Filter by tax types
   * @param {string[]} [params.documentType] - Filter by document types
   * @param {string} [params.seenStatus]     - Filter by seen state: 'NEW' | 'REVIEWED'. Omit for All.
   * @param {string} [params.dateFrom]       - publishDate ISO date (inclusive)
   * @param {string} [params.dateTo]         - publishDate ISO date (inclusive)
   * @param {string} [params.search]         - Keyword search
   * @param {string} [params.sortBy]         - Sort field (publishDate|title|scrapedAt). Use 'publishDate' for composite date sort (publishDate → effectiveDate fallback). Use 'scrapedAt' when nullDates=true.
   * @param {string} [params.sortDir]        - Sort direction (asc|desc)
   * @param {boolean} [params.nullDates]     - When true, returns only docs where both publishDate and effectiveDate are null
   * @returns {Promise<{content: object[], totalPages: number, totalElements: number}>}
   */
  async getAdminDocuments(params = {}) {
    return this.get('/api/v1/admin/documents', params, {
      bypassCache: true,
      skipCache: true,
    })
  }

  /**
   * Fetch per-user New / Reviewed counts for the documents tab.
   * @param {Object} params - Same filter params as getAdminDocuments (excluding seenStatus, page, size).
   * @returns {Promise<{newCount: number, reviewedCount: number, totalCount: number}>}
   */
  async getAdminDocumentsCounts(params = {}) {
    return this.get('/api/v1/admin/documents/counts', params, {
      bypassCache: true,
      skipCache: true,
    })
  }

  /**
   * Toggle the seen/reviewed flag of a single document for the current user.
   * @param {string} documentId
   * @param {boolean} seen
   */
  async updateDocumentSeenStatus(documentId, seen) {
    return this.patch(
      `/api/v1/admin/documents/${encodeURIComponent(documentId)}/seen`,
      { seen },
    )
  }

  /**
   * Bulk toggle the seen/reviewed flag for multiple documents (per-user).
   * @param {string[]} documentIds
   * @param {boolean} seen
   */
  async bulkUpdateDocumentSeenStatus(documentIds, seen) {
    return this.patch('/api/v1/admin/documents/seen/bulk', {
      documentIds,
      seen,
    })
  }

  /**
   * Fetch cascading filter options for the Documents tab.
   * Re-call whenever a parent filter (countries, source, taxType, seenStatus, nullDates) changes.
   * @param {Object} params
   * @param {string[]} [params.countries]    - Narrows: source, taxType, documentType
   * @param {string[]} [params.source]       - Narrows: taxType, documentType
   * @param {string[]} [params.taxType]      - Narrows: documentType
   * @param {string[]} [params.documentType] - Leaf - no downstream narrowing
   * @param {string}   [params.seenStatus]   - 'NEW' | 'REVIEWED' - restricts pool
   * @param {boolean}  [params.nullDates]    - Restrict pool to undated docs
   * @param {string}   [params.dateFrom]     - ISO date
   * @param {string}   [params.dateTo]       - ISO date
   * @param {string}   [params.search]       - Keyword search restriction
   * @returns {Promise<{countries: string[], sources: string[], taxTypes: string[], documentTypes: string[]}>}
   */
  async getDocumentFilterOptions(params = {}) {
    return this.get('/api/v1/admin/documents/filters', params, {
      bypassCache: true,
      skipCache: true,
    })
  }

  /**
   * Fetch cascading filter options for the News Management tab.
   * Re-call on mount and whenever the countries filter changes.
   * @param {Object} params
   * @param {string[]} [params.countries]    - Narrows: source
   * @param {string[]} [params.source]       - Leaf - no downstream narrowing
   * @param {string[]} [params.status]       - Restricts pool (e.g. PENDING_REVIEW)
   * @param {number}   [params.confidenceMin] - Min confidence score
   * @param {number}   [params.confidenceMax] - Max confidence score
   * @param {string}   [params.dateFrom]      - ISO date on pubDate
   * @param {string}   [params.dateTo]        - ISO date on pubDate
   * @returns {Promise<{countries: string[], sources: string[]}>}
   */
  async getNewsFilterOptions(params = {}) {
    return this.get('/api/v1/admin/news/filters', params, {
      bypassCache: true,
      skipCache: true,
    })
  }

  /**
   * Fetch LinkedIn posts for admin review.
   * @param {Object} params
   * @param {number} [params.page]          - 0-based page index
   * @param {number} [params.size]          - Page size
   * @param {string} [params.status]        - Review status filter
   * @param {string[]} [params.source]      - Filter by source/author name(s)
   * @param {string[]} [params.countries]   - Filter by country name(s)
   * @param {string} [params.search]        - Keyword search
   * @param {string} [params.sortBy]        - Sort field (pubDate|source|country)
   * @param {string} [params.sortDir]       - Sort direction (asc|desc)
   * @param {string} [params.dateFrom]      - ISO date string (inclusive)
   * @param {string} [params.dateTo]        - ISO date string (inclusive)
   * @returns {Promise<{content: object[], totalPages: number, totalElements: number}>}
   */
  async getAdminLinkedIn(params = {}) {
    return this.get('/api/v1/admin/linkedin', params, {
      bypassCache: true,
      skipCache: true,
    })
  }

  /**
   * Update review status of a single news article.
   * @param {string} newsId
   * @param {{ reviewStatus: string, countries?: string[] }} reviewData
   */
  async updateNewsReviewStatus(newsId, reviewData) {
    return this.patch(
      `/api/v1/admin/news/${encodeURIComponent(newsId)}/review`,
      reviewData,
    )
  }

  /**
   * Bulk update review status for multiple news articles.
   * @param {string[]} newsIds
   * @param {{ reviewStatus: string }} reviewData
   */
  async bulkUpdateNewsReviewStatus(newsIds, reviewData) {
    return this.patch('/api/v1/admin/news/review/bulk', {
      newsIds,
      ...reviewData,
    })
  }

  /**
   * Update the countries[] array for a news article (full replacement).
   * @param {string} newsId
   * @param {string[]} countries - Full replacement list
   */
  async updateNewsCountry(newsId, countries) {
    return this.patch(
      `/api/v1/admin/news/${encodeURIComponent(newsId)}/country`,
      { countries },
    )
  }
}

// Export singleton instance
const apiService = new ApiService()
export default apiService
